import PropTypes from 'prop-types';
import React from 'react';

import { Header, SideLine, Footer, Background } from '$partials/global/index.js';
import { useAjaxListing } from '$hooks/ajax-listing';
import { AjaxListingContext, InfinitePagination } from '$partials/base/ajax-listing.jsx';
import { ButtonLoadMore } from '$partials/ui';
import { NewsItem } from '$partials/listings/index.js';
import { useSingleSelectFilter } from '$hooks/filters';
import { getBackgroundColours } from '$utilities/site-context';
import { DetailDialogProvider } from '$pages/detail/detail-page-provider';
import { getSidebarTitles } from '$utilities/sidebar-titles';


/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const NewsListingPage = (props) => {
    const page = props.data.page;

    const fundsFilter = useSingleSelectFilter(page?.items?.filters?.funds);
    const fundsFilterSelectedLabel = fundsFilter.options?.find?.(({ slug }) => slug === fundsFilter?.value)?.slug || '';

    const listing = useAjaxListing({
        infinite: true,
        items: page.items?.edges,
        path: `/api/v1/listings/${encodeURIComponent(props.data.uri)}`,
        page: page?.items?.pageInfo?.page,
        totalItems: page.items?.total,
        totalPages: page.items?.totalPages,
        query: {
            'funds': fundsFilter.value,
        }
    });
    return (
        <>
            <Background {...getBackgroundColours()} />

            {/* Navigation */}
            <Header navigation={props.data.navigation} />

            {/* Main content */}
            <main
                className='pt-40 sm:pt-50 md:pt-100 min-h-screen relative'
                id='main-content'>
                <SideLine/>

                <div className='layout-container'>
                    <div className='flex flex-wrap layout-negative-margin'>
                        {/* Heading */}
                        <div className='sm:w-9/12 layout-item-padding'>
                            <h1 className='type-style-2'>
                                {page?.headline}
                            </h1>
                        </div>
                    </div>
                </div>

                <div className='layout-container-padding-right flex flex-wrap max-w-container sm:bg-white mt-40 sm:py-70 md:py-100 rounded-md'>
                    {/* Left Sidebar */}
                    <div className='w-sidebar'>
                        <div className='flex flex-col items-center'>
                            <h2 className='type-style-8 align-middle rotate-180 writing-mode-vertical-rl inline-block'>
                                {getSidebarTitles()?.newsListingSidebarTitle}
                            </h2>
                        </div>
                    </div>

                    {/* Listing */}
                    <DetailDialogProvider>
                        <AjaxListingContext.Provider value={listing}>
                            <AjaxListingContext.Consumer>
                                {(listingState) => (
                                    <>
                                        <section
                                            aria-busy={listingState.loading}
                                            aria-label='News'
                                            className='flex-1 flex flex-wrap self-start'
                                            role='feed'>
                                            {listingState?.items?.length > 0 ? (
                                                <>
                                                    <div className='w-full grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 layout-gap gap-y-20 sm:gap-y-40 md:gap-y-30'>
                                                        {listingState.items.map(({ node: item }) => {
                                                            return (
                                                                <NewsItem
                                                                    className='-mr-20 sm:mr-0'
                                                                    key={item.id}
                                                                    openToModal={true}
                                                                    {...item} />
                                                            );
                                                        })}
                                                    </div>

                                                    {/* Load more button */}
                                                    <InfinitePagination className='mt-20'>
                                                        {'Loading...'}
                                                    </InfinitePagination>

                                                </>
                                            ) : (() => {
                                            /* No results message */
                                                return (
                                                    <>
                                                    </>
                                                );
                                            })()}
                                        </section>
                                    </>
                                )}
                            </AjaxListingContext.Consumer>
                        </AjaxListingContext.Provider>
                    </DetailDialogProvider>
                </div>
            </main>

            {/* Footer */}
            {props.data?.footer && (
                <Footer {...props.data.footer} />
            )}
        </>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

const propTypes = {
    data: PropTypes.shape({
        page: PropTypes.shape({
            __typename: PropTypes.string,
            items: PropTypes.object,
            headline: PropTypes.string.isRequired,
            title: PropTypes.string.isRequired,
        }),
        uri: PropTypes.string,
        navigation: PropTypes.object,
        footer: PropTypes.object,
    }),
    domain: PropTypes.string,
};

NewsListingPage.propTypes = propTypes;

export default NewsListingPage;
